import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  EventQueueAcknowledgement,
  EventQueueAcknowledgementsResponse,
  EventQueueActivityResponse,
  EventQueueBulkActionRequest,
  EventQueueBulkActionResult,
  EventQueueConcurrencyResponse,
  EventQueueEventDetail,
  EventQueueEventsResponse,
  EventQueueFailureRange,
  EventQueueFailuresResponse,
  EventQueueJobState,
  EventQueueMetricsRange,
  EventQueueMetricsResponse,
  EventQueueOverviewResponse,
  EventQueueQueueDetailResponse,
  EventQueuePauseResponse,
  EventQueueShadowAcknowledgeResponse,
  EventQueueShadowContextResponse,
  EventQueueShadowResponse,
  EventQueueStorageResponse,
  EventQueueBackgroundJobsResponse,
  EventQueueIngressRange,
  EventQueueIngressResponse,
  EventQueueWorkersResponse,
  InboundEventStatus,
} from "@t3tools/contracts";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";

export type EventQueueEventFilters = {
  /** Qualifier search; the server answers 400 for one it can't read. */
  q?: string | undefined;
  queueName?: string | undefined;
  ruleId?: string | undefined;
  source?: string | undefined;
  state?: EventQueueJobState | undefined;
  status?: InboundEventStatus | undefined;
  subjectKey?: string | undefined;
};

/** The overview, storage and job lists refresh this often while the page is open and visible. */
export const EVENT_QUEUE_REFRESH_MS = 10_000;
export const EVENT_QUEUE_ACTIVE_EVENT_REFRESH_MS = 1_000;
export const EVENT_QUEUE_PAGE_SIZE = 50;
/** The comparison job runs every 15 minutes; the report itself is recomputed per request. */
export const EVENT_QUEUE_SHADOW_REFRESH_MS = 60_000;
/** Metrics buckets are minutes to hours wide, so they don't need the 10s refresh. */
export const EVENT_QUEUE_METRICS_REFRESH_MS = 30_000;

export const eventQueueQueryKeys = {
  all: [...operatorsQueryKeys.all, "event-queue"] as const,
  overview: () => [...eventQueueQueryKeys.all, "overview"] as const,
  acknowledgements: () => [...eventQueueQueryKeys.all, "acknowledgements"] as const,
  queue: (name: string) => [...eventQueueQueryKeys.all, "queue", name] as const,
  metrics: (name: string, range: EventQueueMetricsRange) =>
    [...eventQueueQueryKeys.all, "queue", name, "metrics", range] as const,
  storage: () => [...eventQueueQueryKeys.all, "storage"] as const,
  events: (filters: EventQueueEventFilters) =>
    [
      ...eventQueueQueryKeys.all,
      "events",
      filters.status ?? null,
      filters.source ?? null,
      filters.ruleId ?? null,
      filters.queueName ?? null,
      filters.state ?? null,
      filters.subjectKey ?? null,
      filters.q ?? null,
    ] as const,
  event: (id: string) => [...eventQueueQueryKeys.all, "event", id] as const,
  failures: (queueName: string | null, range: EventQueueFailureRange) =>
    [...eventQueueQueryKeys.all, "failures", queueName, range] as const,
  workers: () => [...eventQueueQueryKeys.all, "workers"] as const,
  ingress: (range: EventQueueIngressRange) =>
    [...eventQueueQueryKeys.all, "ingress", range] as const,
  backgroundJobs: () => [...eventQueueQueryKeys.all, "background-jobs"] as const,
  activity: (filters: EventQueueActivityFilters) =>
    [
      ...eventQueueQueryKeys.all,
      "activity",
      filters.queueName ?? null,
      filters.actor ?? null,
    ] as const,
  shadow: () => [...eventQueueQueryKeys.all, "shadow", "github"] as const,
  shadowContext: (input: EventQueueShadowContextInput) =>
    [
      ...eventQueueQueryKeys.shadow(),
      "context",
      input.repo,
      input.prNumber,
      input.at,
      input.receiptId ?? null,
    ] as const,
};

export type EventQueueShadowContextInput = {
  at: string;
  prNumber: number;
  receiptId?: string | null;
  repo: string;
};

/** A 409 from an action: the event or queue moved on since the page last refreshed. */
export class EventQueueConflictError extends Error {
  override readonly name = "EventQueueConflictError";
}

export class EventQueueNotFoundError extends Error {
  override readonly name = "EventQueueNotFoundError";
}

/** A 400 or 413: the request itself needs changing, such as a search typo or too big a selection. */
export class EventQueueRequestError extends Error {
  override readonly name = "EventQueueRequestError";
}

const FORBIDDEN_MESSAGE = "You don't have access to Queues.";

async function eventQueueFetch<T>(input: {
  fallbackError: string;
  init?: RequestInit;
  path: string;
}): Promise<T> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/event-queue${input.path}`, {
    credentials: "include",
    ...input.init,
  });
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (!response.ok) {
    const message =
      (await readOperatorsAuthErrorMessage(response).catch(() => "")) || input.fallbackError;
    if (response.status === 403) throw new OperatorsForbiddenError(message || FORBIDDEN_MESSAGE);
    if (response.status === 404) throw new EventQueueNotFoundError(message);
    if (response.status === 409) throw new EventQueueConflictError(message);
    if (response.status === 400 || response.status === 413) {
      throw new EventQueueRequestError(message);
    }
    throw new Error(message);
  }
  // No Content: the action worked and there's nothing to read.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const postJson = (body?: unknown): RequestInit =>
  body === undefined
    ? { method: "POST" }
    : {
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      };

/** Forbidden, not-found and bad-request answers won't change on retry, so don't hammer the API with them. */
function retryTransient(failureCount: number, error: unknown): boolean {
  if (
    error instanceof OperatorsForbiddenError ||
    error instanceof EventQueueNotFoundError ||
    error instanceof EventQueueRequestError
  ) {
    return false;
  }
  return failureCount < 2;
}

export function eventQueueOverviewQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.overview(),
    queryFn: () =>
      eventQueueFetch<EventQueueOverviewResponse>({
        fallbackError: "Could not load the queues overview.",
        path: "/overview",
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueAcknowledgementsQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.acknowledgements(),
    queryFn: () =>
      eventQueueFetch<EventQueueAcknowledgementsResponse>({
        fallbackError: "Could not load what's acknowledged.",
        path: "/acknowledgements",
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function acknowledgeInboxItemMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (input: { expiresInMinutes: number | null; itemKey: string; note?: string }) =>
      eventQueueFetch<EventQueueAcknowledgement>({
        fallbackError: "Could not acknowledge it.",
        init: postJson(input),
        path: "/acknowledgements",
      }),
    onSettled: async () =>
      queryClient.invalidateQueries({ queryKey: eventQueueQueryKeys.acknowledgements() }),
  });
}

export function clearInboxAcknowledgementMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (itemKey: string) =>
      eventQueueFetch<void>({
        fallbackError: "Could not bring it back.",
        init: postJson({ itemKey }),
        path: "/acknowledgements/clear",
      }).catch((error: unknown) => {
        // Someone else already brought it back, or it expired: the result is the same.
        if (!(error instanceof EventQueueNotFoundError)) throw error;
      }),
    onSettled: async () =>
      queryClient.invalidateQueries({ queryKey: eventQueueQueryKeys.acknowledgements() }),
  });
}

export function eventQueueQueueQueryOptions(name: string) {
  return queryOptions({
    queryKey: eventQueueQueryKeys.queue(name),
    queryFn: () =>
      eventQueueFetch<EventQueueQueueDetailResponse>({
        fallbackError: `Could not load ${name}.`,
        path: `/queues/${encodeURIComponent(name)}`,
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueMetricsQueryOptions(name: string, range: EventQueueMetricsRange) {
  return queryOptions({
    queryKey: eventQueueQueryKeys.metrics(name, range),
    queryFn: () =>
      eventQueueFetch<EventQueueMetricsResponse>({
        fallbackError: `Could not load metrics for ${name}.`,
        path: `/queues/${encodeURIComponent(name)}/metrics?range=${range}`,
      }),
    refetchInterval: EVENT_QUEUE_METRICS_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 10_000,
  });
}

export function eventQueueStorageQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.storage(),
    queryFn: () =>
      eventQueueFetch<EventQueueStorageResponse>({
        fallbackError: "Could not load storage.",
        path: "/storage",
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueEventsPath(
  filters: EventQueueEventFilters,
  cursor: string | null,
): string {
  const params = new URLSearchParams({ limit: String(EVENT_QUEUE_PAGE_SIZE) });
  if (filters.status) params.set("status", filters.status);
  if (filters.source) params.set("source", filters.source);
  if (filters.ruleId) params.set("ruleId", filters.ruleId);
  if (filters.queueName) params.set("queue", filters.queueName);
  if (filters.state) params.set("state", filters.state);
  if (filters.subjectKey) params.set("subject", filters.subjectKey);
  if (filters.q) params.set("q", filters.q);
  if (cursor) params.set("cursor", cursor);
  return `/events?${params.toString()}`;
}

export function eventQueueFailuresPath(
  queueName: string | null,
  range: EventQueueFailureRange,
): string {
  const params = new URLSearchParams({ range });
  if (queueName) params.set("queue", queueName);
  return `/failures?${params.toString()}`;
}

export function eventQueueFailuresQueryOptions(
  queueName: string | null,
  range: EventQueueFailureRange,
) {
  return queryOptions({
    queryKey: eventQueueQueryKeys.failures(queueName, range),
    queryFn: () =>
      eventQueueFetch<EventQueueFailuresResponse>({
        fallbackError: "Could not load failures.",
        path: eventQueueFailuresPath(queueName, range),
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

/** Heartbeats land every 10 seconds, so the Workers tab refreshes at the same pace. */
export function eventQueueWorkersQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.workers(),
    queryFn: () =>
      eventQueueFetch<EventQueueWorkersResponse>({
        fallbackError: "Could not load workers.",
        path: "/workers",
      }),
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueIngressQueryOptions(range: EventQueueIngressRange) {
  return queryOptions({
    queryKey: eventQueueQueryKeys.ingress(range),
    queryFn: () =>
      eventQueueFetch<EventQueueIngressResponse>({
        fallbackError: "Could not load ingress.",
        path: `/ingress?range=${range}`,
      }),
    refetchInterval: EVENT_QUEUE_METRICS_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 10_000,
  });
}

/** Also read for the Inbox, so it refreshes on the slower metrics cadence. */
export function eventQueueBackgroundJobsQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.backgroundJobs(),
    queryFn: () =>
      eventQueueFetch<EventQueueBackgroundJobsResponse>({
        fallbackError: "Could not load background jobs.",
        path: "/background-jobs",
      }),
    refetchInterval: EVENT_QUEUE_METRICS_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 10_000,
  });
}

export type EventQueueActivityFilters = {
  actor?: string | undefined;
  queueName?: string | undefined;
};

export function eventQueueActivityInfiniteQueryOptions(filters: EventQueueActivityFilters) {
  return infiniteQueryOptions({
    queryKey: eventQueueQueryKeys.activity(filters),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.queueName) params.set("queue", filters.queueName);
      if (filters.actor) params.set("actor", filters.actor);
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();
      return eventQueueFetch<EventQueueActivityResponse>({
        fallbackError: "Could not load activity.",
        path: `/activity${query ? `?${query}` : ""}`,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueEventsInfiniteQueryOptions(filters: EventQueueEventFilters) {
  return infiniteQueryOptions({
    queryKey: eventQueueQueryKeys.events(filters),
    queryFn: ({ pageParam }) =>
      eventQueueFetch<EventQueueEventsResponse>({
        fallbackError: "Could not load events.",
        path: eventQueueEventsPath(filters, pageParam),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchInterval: EVENT_QUEUE_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

export function eventQueueEventQueryOptions(id: string) {
  return queryOptions({
    queryKey: eventQueueQueryKeys.event(id),
    queryFn: () =>
      eventQueueFetch<EventQueueEventDetail>({
        fallbackError: "Could not load the event.",
        path: `/events/${encodeURIComponent(id)}`,
      }),
    // An open event that's waiting or running is followed closely so a retry visibly completes.
    refetchInterval: (query) =>
      query.state.data?.status === "queued" || query.state.data?.status === "processing"
        ? EVENT_QUEUE_ACTIVE_EVENT_REFRESH_MS
        : EVENT_QUEUE_REFRESH_MS,
    retry: retryTransient,
    staleTime: 3_000,
  });
}

async function refreshAfterAction(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: eventQueueQueryKeys.all });
}

/**
 * Retry and discard answer with the event's row but not its attempts, facts or
 * related events, so the row is merged over the open sheet's data until the
 * refetch fills the rest in.
 */
function mergeEventAfterAction(
  queryClient: QueryClient,
  event: Partial<EventQueueEventDetail> & { id: string },
): void {
  queryClient.setQueryData<EventQueueEventDetail>(eventQueueQueryKeys.event(event.id), (current) =>
    current ? { ...current, ...event } : undefined,
  );
}

export function retryEventQueueEventMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (id: string) =>
      eventQueueFetch<EventQueueEventDetail>({
        fallbackError: "Could not retry the event.",
        init: postJson(),
        path: `/events/${encodeURIComponent(id)}/retry`,
      }),
    onSuccess: async (event) => {
      mergeEventAfterAction(queryClient, event);
      await refreshAfterAction(queryClient);
    },
    onError: async () => refreshAfterAction(queryClient),
  });
}

export function discardEventQueueEventMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (id: string) =>
      eventQueueFetch<EventQueueEventDetail>({
        fallbackError: "Could not discard the event.",
        init: postJson(),
        path: `/events/${encodeURIComponent(id)}/discard`,
      }),
    onSuccess: async (event) => {
      mergeEventAfterAction(queryClient, event);
      await refreshAfterAction(queryClient);
    },
    onError: async () => refreshAfterAction(queryClient),
  });
}

export function bulkEventQueueMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (input: { action: "discard" | "retry"; request: EventQueueBulkActionRequest }) =>
      eventQueueFetch<EventQueueBulkActionResult>({
        fallbackError: `Could not ${input.action} these jobs.`,
        init: postJson(input.request),
        path: `/bulk/${input.action}`,
      }),
    onSettled: async () => refreshAfterAction(queryClient),
  });
}

export function runBackgroundJobMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (key: string) =>
      eventQueueFetch<{ key: string; started: true }>({
        fallbackError: "Could not start it.",
        init: postJson(),
        path: `/background-jobs/${encodeURIComponent(key)}/run`,
      }),
    onSettled: async () =>
      queryClient.invalidateQueries({ queryKey: eventQueueQueryKeys.backgroundJobs() }),
  });
}

export function setEventQueueConcurrencyMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (input: { concurrency: number; queueName: string }) =>
      eventQueueFetch<EventQueueConcurrencyResponse>({
        fallbackError: "Could not change how many run at once.",
        init: postJson({ concurrency: input.concurrency }),
        path: `/queues/${encodeURIComponent(input.queueName)}/concurrency`,
      }),
    onSettled: async () => refreshAfterAction(queryClient),
  });
}

export function pauseEventQueueMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (input: { queueName: string; reason: string }) =>
      eventQueueFetch<EventQueuePauseResponse>({
        fallbackError: "Could not pause the queue.",
        init: postJson(input.reason.trim() ? { reason: input.reason.trim() } : {}),
        path: `/queues/${encodeURIComponent(input.queueName)}/pause`,
      }),
    onSettled: async () => refreshAfterAction(queryClient),
  });
}

export function resumeEventQueueMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (queueName: string) =>
      eventQueueFetch<EventQueuePauseResponse>({
        fallbackError: "Could not resume the queue.",
        init: postJson(),
        path: `/queues/${encodeURIComponent(queueName)}/resume`,
      }),
    onSettled: async () => refreshAfterAction(queryClient),
  });
}

export function eventQueueShadowQueryOptions() {
  return queryOptions({
    queryKey: eventQueueQueryKeys.shadow(),
    queryFn: () =>
      eventQueueFetch<EventQueueShadowResponse>({
        fallbackError: "Could not load the shadow match report.",
        path: "/shadow/github",
      }),
    refetchInterval: EVENT_QUEUE_SHADOW_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: retryTransient,
    staleTime: 15_000,
  });
}

/** A gap's nearby events; fetched when its row is expanded, and kept since history doesn't change. */
export function eventQueueShadowContextQueryOptions(input: EventQueueShadowContextInput) {
  const params = new URLSearchParams({
    at: input.at,
    prNumber: String(input.prNumber),
    repo: input.repo,
  });
  if (input.receiptId) params.set("receiptId", input.receiptId);
  return queryOptions({
    queryKey: eventQueueQueryKeys.shadowContext(input),
    queryFn: () =>
      eventQueueFetch<EventQueueShadowContextResponse>({
        fallbackError: "Could not load what happened around this gap.",
        path: `/shadow/github/context?${params.toString()}`,
      }),
    retry: retryTransient,
    staleTime: 60_000,
  });
}

export function acknowledgeShadowMismatchMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (input: { id: string; reason: string }) =>
      eventQueueFetch<EventQueueShadowAcknowledgeResponse>({
        fallbackError: "Could not acknowledge the mismatch.",
        init: postJson({ reason: input.reason }),
        path: `/shadow/github/mismatches/${encodeURIComponent(input.id)}/acknowledge`,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: eventQueueQueryKeys.shadow() });
    },
  });
}
